import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Stack,
  Alert,
  Card,
  CardContent,
  Chip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch
} from '@mui/material';
import { useFilters } from '@/contexts/FilterContext';
import { apiService } from '@/services/api';
import { useMessage } from '@/contexts/MessageContext';
import { useRefresh } from '@/contexts/RefreshContext';
import { useConfirmation } from '@/contexts/ConfirmationContext';
import { ProductCell } from '@/components/ProductCell';
import { useProductsMap } from '@/hooks/useProductsMap';
import { ProductScopeGate } from '@/components/ProductScopeGate';

interface SystemAdminRow {
  id: string;
  email: string;
  isActive: boolean;
  lastLogin?: string;
  createdAt: string;
}

export const ProductSystemAdminsTab: React.FC = () => {
  const { productId } = useFilters();
  const { showError, showSuccess } = useMessage();
  const { setRefreshCallback } = useRefresh();
  const { confirm } = useConfirmation();
  const productsById = useProductsMap();
  const [rows, setRows] = useState<SystemAdminRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<unknown>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const [resetTarget, setResetTarget] = useState<SystemAdminRow | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetting, setResetting] = useState(false);

  const load = useCallback(async () => {
    if (!productId?.trim()) return;
    setLoading(true);
    setLoadError(null);
    try {
      const res = await apiService.listSystemAdminUsers();
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      setRows([]);
      setLoadError(err);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    load().catch(() => undefined);
  }, [load, productId]);

  useEffect(() => {
    setRefreshCallback(load);
    return () => setRefreshCallback(null);
  }, [load, setRefreshCallback]);

  const handleOpenCreate = () => {
    setNewEmail('');
    setNewPassword('');
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!newEmail.trim() || !newEmail.includes('@')) {
      showError('Informe um email válido');
      return;
    }
    if (newPassword.length < 8) {
      showError('A senha precisa ter ao menos 8 caracteres');
      return;
    }
    setSaving(true);
    try {
      await apiService.createSystemAdminUser({ email: newEmail.trim(), password: newPassword });
      showSuccess('System admin criado com sucesso');
      setCreateOpen(false);
      await load();
    } catch (err: any) {
      showError(err?.response?.data?.message || 'Erro ao criar system admin');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (row: SystemAdminRow) => {
    try {
      await apiService.setSystemAdminUserActive(row.id, !row.isActive);
      showSuccess(row.isActive ? 'System admin desativado' : 'System admin ativado');
      await load();
    } catch {
      showError('Erro ao atualizar status');
    }
  };

  const handleOpenReset = (row: SystemAdminRow) => {
    setResetTarget(row);
    setResetPassword('');
  };

  const handleReset = async () => {
    if (!resetTarget) return;
    if (resetPassword.length < 8) {
      showError('A senha precisa ter ao menos 8 caracteres');
      return;
    }
    setResetting(true);
    try {
      await apiService.resetSystemAdminUserPassword(resetTarget.id, resetPassword);
      showSuccess('Senha redefinida com sucesso');
      setResetTarget(null);
    } catch (err: any) {
      showError(err?.response?.data?.message || 'Erro ao redefinir senha');
    } finally {
      setResetting(false);
    }
  };

  const handleDelete = (row: SystemAdminRow) => {
    confirm({
      title: 'Remover system admin',
      message: `Remover o acesso de system admin de ${row.email}? Essa conta não conseguirá mais fazer login nem impersonar tenants deste produto.`,
      confirmText: 'Remover',
      cancelText: 'Cancelar',
      confirmColor: 'error',
      onConfirm: async () => {
        try {
          await apiService.deleteSystemAdminUser(row.id);
          showSuccess('System admin removido');
          await load();
        } catch {
          showError('Erro ao remover system admin');
        }
      }
    });
  };

  return (
    <ProductScopeGate
      productId={productId}
      integration="core"
      loading={loading}
      loadError={loadError}
      isEmpty={false}
      onRetry={load}
    >
      <Box>
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Stack spacing={1.5}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                System Admins do produto
              </Typography>
              <ProductCell
                productId={productId}
                name={productsById.get(productId)?.displayName}
                avatarUrl={productsById.get(productId)?.avatarUrl}
              />
              <Alert severity="info">
                Contas sem tenant vinculado, usadas pelo suporte para login com 2FA por email e
                impersonação de clientes deste produto. Diferente de usuário de tenant — não
                pertence a nenhum condomínio/cliente.
              </Alert>
            </Stack>
          </CardContent>
        </Card>

        <Stack direction="row" sx={{ mb: 2 }}>
          <Button variant="contained" onClick={handleOpenCreate}>
            Novo system admin
          </Button>
        </Stack>

        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Último login</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Alert severity="info" sx={{ my: 1 }}>
                      Nenhum system admin cadastrado para este produto ainda.
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map(row => (
                  <TableRow key={row.id}>
                    <TableCell>{row.email}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Switch
                          size="small"
                          checked={row.isActive}
                          onChange={() => handleToggleActive(row)}
                        />
                        <Chip
                          size="small"
                          color={row.isActive ? 'success' : 'default'}
                          label={row.isActive ? 'Ativo' : 'Inativo'}
                        />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {row.lastLogin ? new Date(row.lastLogin).toLocaleString('pt-BR') : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" onClick={() => handleOpenReset(row)}>
                          Redefinir senha
                        </Button>
                        <Button size="small" color="error" onClick={() => handleDelete(row)}>
                          Remover
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>

        <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Novo system admin</DialogTitle>
          <DialogContent dividers>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
              margin="normal"
              autoFocus
            />
            <TextField
              fullWidth
              label="Senha"
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              margin="normal"
              helperText="Mínimo 8 caracteres"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={handleCreate} disabled={saving}>
              {saving ? 'Criando...' : 'Criar'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={!!resetTarget} onClose={() => setResetTarget(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Redefinir senha</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {resetTarget?.email}
            </Typography>
            <TextField
              fullWidth
              label="Nova senha"
              type="password"
              value={resetPassword}
              onChange={e => setResetPassword(e.target.value)}
              margin="normal"
              helperText="Mínimo 8 caracteres"
              autoFocus
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResetTarget(null)} disabled={resetting}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={handleReset} disabled={resetting}>
              {resetting ? 'Salvando...' : 'Redefinir'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ProductScopeGate>
  );
};

export default ProductSystemAdminsTab;
